import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import Button from './UIKit/Button';
import { useAuth } from '../context/AuthContext';
import { authAPI, usersAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation('auth');
  const [accountDisabled, setAccountDisabled] = useState(false);

  useEffect(() => {
    let isActive = true;
    let isProcessing = false;

    const handleOAuthCallback = async () => {
      // Предотвращаем повторное выполнение
      if (isProcessing) {
        return;
      }

      try {
        // Parse access token from URL (refresh token is now in HTTP-only cookie)
        const params = new URLSearchParams(window.location.search);
        const accessToken = params.get('accessToken');
        const error = params.get('error');

        // Проверяем, если аккаунт заблокирован
        if (error === 'account_disabled') {
          setAccountDisabled(true);
          return;
        }

        // Если токена нет, проверяем localStorage - может быть авторизация уже завершена
        if (!accessToken) {
          const existingToken = localStorage.getItem('token');
          const existingUser = localStorage.getItem('user');

          if (existingToken && existingUser) {
            navigate('/', { replace: true });
            return;
          }
        }

        isProcessing = true;

        if (accessToken && isActive) {
          // Store access token
          localStorage.setItem('token', accessToken);

          try {
            await authAPI.exchangeToken(accessToken);
          } catch (exchangeError) {
            console.error('Failed to exchange token:', exchangeError);
            // Продолжаем выполнение, так как accessToken уже сохранен
          }

          // Get user profile from auth service
          const response = await authAPI.handleOAuthCallback(accessToken);

          if (response && response.user && isActive) {
            const user = response.user;

            // Sync user data with courses service (same as normal login flow)
            try {
              const syncRes = await usersAPI.syncOrCreateUser({
                userId: user._id || user.id,
                email: user.email,
                username: user.fullName || user.username,
                roles: user.roles || [],
                avatar: user.avatar
              });

              if (syncRes.data.user && isActive) {
                // Update user data with synced data
                const syncedUser = syncRes.data.user;
                syncedUser.roles = user.roles || [];
                setUser(syncedUser);
                localStorage.setItem('user', JSON.stringify(syncedUser));
              } else if (isActive) {
                // Fallback to original user data
                setUser(user);
                localStorage.setItem('user', JSON.stringify(user));
              }
            } catch (syncErr) {
              console.error('User sync error:', syncErr);
              if (isActive) {
                // Fallback to original user data
                setUser(user);
                localStorage.setItem('user', JSON.stringify(user));
              }
            }

            if (isActive) {
              // Очищаем URL от токена (безопасность)
              window.history.replaceState({}, document.title, window.location.pathname);

              toast.success(t('login_success'));

              // Add a small delay to ensure user state is properly set
              setTimeout(() => {
                if (isActive) {
                  navigate('/', { replace: true });
                }
              }, 100);
            }
          } else if (isActive) {
            console.error('OAuth callback: Invalid user data');
            navigate('/login?error=oauth', { replace: true });
          }
        } else if (isActive) {
          // Handle error: access token missing
          console.error('OAuth callback: Missing access token');
          navigate('/login?error=oauth', { replace: true });
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        if (isActive) {
          navigate('/login?error=oauth', { replace: true });
        }
      }
    };

    handleOAuthCallback();

    return () => {
      isActive = false;
    };
  }, [navigate, setUser, t]);

  // Если аккаунт заблокирован, показываем соответствующее сообщение
  if (accountDisabled) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          bgcolor: 'background.default',
        }}
      >
        <Box
          sx={{
            maxWidth: 550,
            width: '100%',
            p: 4,
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: 3,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'warning.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 3,
              }}
            >
              <LockIcon sx={{ fontSize: 40, color: 'common.white' }} />
            </Box>
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t('account_disabled_title', 'Account Disabled')}
          </Typography>

          {/* Message */}
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {t('account_disabled_message', 'Your account is currently disabled. Please contact the administrator to request access.')}
            </Typography>
          </Alert>

          {/* Instructions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2,
              mb: 3,
              borderRadius: 2,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <EmailIcon sx={{ color: 'primary.main', mt: 0.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {t('account_disabled_instructions', 'To get access, please reach out to your system administrator with your email address.')}
            </Typography>
          </Box>

          {/* Button */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => navigate('/login', { replace: true })}
            sx={{ py: 1.5 }}
          >
            {t('back_to_login', 'Back to Login')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress size={32} />
      <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
        Signing you in with Google...
      </Typography>
    </Box>
  );
};

export default OAuthCallback; 